/** Contracts tab for the Customer Workspace (Demo Mode only). Editable contract terms, renewal
 * dates, notice periods, SLAs, obligations and key contract milestones, structured as
 * traditional form fields (bound to the parent workspace's edit draft - see `../index.tsx`).
 * SLAs/obligations are repeatable free-text lines (`EditableStringList`); milestones are a small
 * editable table since they're structured, repeatable (label + date) records. Also surfaces
 * downloadable contract and legal agreement documents via the shared `DocumentChecklist`
 * component (reused from the OFD onboarding wizard's due diligence structure). Rendered inside
 * the single Section the parent workspace places directly below its Tabs. */

import {
  Box, Grid, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import { CustomerRecord, getCustomerDocuments } from '../../../../mocks/data/customerManagementData';
import EditableStringList from '../../components/EditableStringList';
import DocumentChecklist from '../../components/DocumentChecklist';

interface ContractsTabProps {
  customer: CustomerRecord;
  onChange: (updater: (prev: CustomerRecord) => CustomerRecord) => void;
}

const CONTRACT_STATUSES: CustomerRecord['contract']['status'][] = ['Active', 'Renewal Due'];

const ContractsTab = ({ customer, onChange }: ContractsTabProps) => {
  const { t } = useTranslation('common');
  const { contract } = customer;

  const updateContract = (patch: Partial<CustomerRecord['contract']>) => {
    onChange((prev) => ({ ...prev, contract: { ...prev.contract, ...patch } }));
  };

  const updateMilestone = (index: number, patch: Partial<CustomerRecord['contract']['milestones'][number]>) => {
    updateContract({ milestones: contract.milestones.map((milestone, i) => (i === index ? { ...milestone, ...patch } : milestone)) });
  };

  const removeMilestone = (index: number) => {
    updateContract({ milestones: contract.milestones.filter((_, i) => i !== index) });
  };

  const addMilestone = () => {
    updateContract({ milestones: [...contract.milestones, { milestone: '', date: '' }] });
  };

  return (
    <Section style={{ p: 5 }}>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}
          md={6}>
          <Typography variant="textSM"
            component="div"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('contractsDetailsTitle', 'Contract details')}</Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}>
              <TextField
                label={t('contractsRef', 'Contract Ref.')}
                value={contract.contractRef}
                onChange={(e) => updateContract({ contractRef: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={6}>
              <TextField
                label={t('contractsStartDate', 'Start Date')}
                type="date"
                value={contract.startDate}
                onChange={(e) => updateContract({ startDate: e.target.value })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item
              xs={6}>
              <TextField
                label={t('contractsEndDate', 'End Date')}
                type="date"
                value={contract.endDate}
                onChange={(e) => updateContract({ endDate: e.target.value })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item
              xs={6}>
              <TextField
                label={t('contractsNoticePeriod', 'Notice Period')}
                value={contract.noticePeriod}
                onChange={(e) => updateContract({ noticePeriod: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={6}>
              <TextField
                label={t('contractsRenewalTimer', 'Renewal Timer')}
                value={contract.renewalTimer}
                onChange={(e) => updateContract({ renewalTimer: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                select
                label={t('contractsStatus', 'Status')}
                value={contract.status}
                onChange={(e) => updateContract({ status: e.target.value as CustomerRecord['contract']['status'] })}
                fullWidth
                size="small"
              >
                {CONTRACT_STATUSES.map((status) => (
                  <MenuItem key={status}
                    value={status}>{status}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={6}>
          <EditableStringList
            label={t('contractsSlas', 'SLAs')}
            items={contract.slas}
            onChange={(slas) => updateContract({ slas })}
            addLabel={t('contractsAddSla', 'Add SLA')}
            placeholder={t('contractsSlaPlaceholder', 'e.g. Service incident response: 4 hours')}
          />
          <Box sx={{ mt: 3 }}>
            <EditableStringList
              label={t('contractsObligations', 'Obligations')}
              items={contract.obligations}
              onChange={(obligations) => updateContract({ obligations })}
              addLabel={t('contractsAddObligation', 'Add obligation')}
              placeholder={t('contractsObligationPlaceholder', 'e.g. Quarterly nutrition reporting')}
            />
          </Box>
        </Grid>

        <Grid item
          xs={12}>
          <Stack direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}>
            <Typography variant="textSM"
              component="div"
              fontWeight={600}>{t('contractsMilestonesTitle', 'Key milestones')}</Typography>
            <IconButton size="small"
              onClick={addMilestone}
              title={t('contractsAddMilestone', 'Add milestone')}>
              <Add fontSize="small" />
            </IconButton>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('contractsMilestone', 'Milestone')}</TableCell>
                  <TableCell>{t('contractsMilestoneDate', 'Date')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {contract.milestones.map((milestone, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        value={milestone.milestone}
                        onChange={(e) => updateMilestone(index, { milestone: e.target.value })}
                        size="small"
                        fullWidth
                        sx={{ mb: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="date"
                        value={milestone.date}
                        onChange={(e) => updateMilestone(index, { date: e.target.value })}
                        size="small"
                        fullWidth
                        sx={{ mb: 0 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small"
                        onClick={() => removeMilestone(index)}>
                        <DeleteOutline fontSize="small"
                          color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item
          xs={12}>
          <DocumentChecklist
            documents={getCustomerDocuments(customer)}
            headerFilter="Contracts & Legal Agreements"
            mode="view"
          />
        </Grid>
      </Grid>
    </Section>
  );
};

export default ContractsTab;
